import React, { ReactElement } from 'react'
import { Divider, Flex, Text } from 'theme-ui'
import { ExternalLink, Svg } from 'ui'
import { Link } from 'gatsby'
import { FooterAction, FooterLink, footerLinks } from './OrgStaticFooterData'
import { DOCS_CONTROL_D_DOMAIN } from 'gatsby-env-variables'
import LogoBgIcon from 'images/organization/homepage/footer/logo-icon.svg'
import CONTROLDLogo from 'ui/CONTROLD'
import LogoIcon from 'images/logos/org-homepage.svg'
import DiscordIcon from 'images/organization/homepage/footer/discord.svg'
import RedditIcon from 'images/organization/homepage/footer/reddit.svg'
import XIcon from 'images/organization/homepage/footer/x.svg'
import CertificationBadges from 'components/Organization/HomePage/OrgStaticFooter/CertificationBadges'

const copyrightYear = new Date().getFullYear()

export default function OrgStaticPageFooter(): ReactElement {
  return (
    <Flex
      data-testid="static-page-footer-section"
      className="ios-scrolling-glitches-fix"
      sx={{
        position: 'relative',
        width: '100%',
        flexDirection: 'column',
        pt: ['8rem', '10rem'],
        pb: [0, '2.4rem'],
        px: [0, '1.6rem', 0],
        alignItems: 'center',
        borderTop: '1px solid',
        borderColor: 'ceruleanFrost15',
        overflow: 'hidden',
        backgroundColor: 'blue800',
      }}
    >
      <Svg
        svg={LogoBgIcon}
        descriptiveText="Decorative background logo"
        sx={{
          visibility: ['hidden', 'hidden', 'visible'],
          position: 'absolute',
          top: 0,
          right: 0,
          zIndex: 0,
        }}
      />
      <Flex
        sx={{
          maxWidth: ['100%', '112rem'],
          width: '100%',
          minWidth: 0,
          mb: '1.6rem',
          justifyContent: 'space-between',
          flexDirection: ['column', 'row'],
          textAlign: ['center', 'left'],
          zIndex: 'zIndex5',
        }}
      >
        <FooterSubsection
          title={footerLinks.general.title}
          links={footerLinks.general.links}
          actions={footerLinks.general.actions}
        />
        <FooterSubsection title={footerLinks.features.title} links={footerLinks.features.links} />
        <FooterSubsection
          title={footerLinks.industries.title}
          links={footerLinks.industries.links}
        />
        <FooterSubsection title={footerLinks.resources.title} links={footerLinks.resources.links} />
        <CertificationBadges />
      </Flex>
      <Divider
        sx={{
          width: '100%',
          maxWidth: ['100%', '112rem'],
          height: '0.2rem',
          color: 'ceruleanFrost15',
          mt: ['0.8rem', 0],
          mb: ['6.4rem', '3.2rem'],
        }}
      />
      <Flex
        sx={{
          width: '100%',
          maxWidth: ['100%', '112rem'],
          flexDirection: ['column', 'row'],
          alignItems: 'center',
          justifyContent: 'space-between',
          zIndex: 'zIndex5',
          mb: ['4rem', '4rem', '5.6rem'],
          gap: '3.2rem',
        }}
      >
        <Flex
          sx={{
            gap: ['3.2rem', '1.6rem'],
            alignItems: 'center',
            fontSize: '1.3rem',
            fontWeight: 500,
            color: 'aliceBlue70',
            lineHeight: '136%',
            flexDirection: ['column', 'row'],
          }}
        >
          <Svg svg={LogoIcon} descriptiveText="CONTROLD company logo" />
          <Text sx={{ whiteSpace: 'nowrap' }}>
            © {copyrightYear}{' '}
            <CONTROLDLogo
              CONTROLsx={{
                alignSelf: 'center',
                fontWeight: 500,
                letterSpacing: 0,
              }}
              Dsx={{ color: 'aliceBlue70', ml: '0.4rem' }}
            />
            , Inc.
          </Text>
        </Flex>
        <Flex sx={{ gap: '2.4rem' }}>
          <SocialMediaButton to="https://twitter.com/controldns" icon={XIcon} />
          <SocialMediaButton to="https://www.reddit.com/r/ControlD/" icon={RedditIcon} />
          <SocialMediaButton to="https://discord.gg/dns" icon={DiscordIcon} />
        </Flex>
      </Flex>
    </Flex>
  )
}

function FooterSubsection({
  title,
  links,
}: {
  title: string
  links: FooterLink[]
  actions?: FooterAction[]
}): ReactElement {
  return (
    <Flex
      sx={{
        flexDirection: 'column',
        mb: '4rem',
        gap: '1.6rem',
        a: {
          textDecoration: 'none',
          color: 'white50',
        },
      }}
    >
      <Text sx={{ color: 'aliceBlue70', fontSize: '1.3rem' }}>{title}</Text>
      <LinksSubsection links={links} />
    </Flex>
  )
}

function LinksSubsection({ links }: { links: FooterLink[] }): ReactElement {
  return (
    <>
      {links.map(({ to, text, isExternal }) =>
        isExternal ? (
          <ExternalLink
            key={text}
            to={to.includes('https') ? to : `${DOCS_CONTROL_D_DOMAIN}/docs${to}`}
            aria-label={text}
          >
            <Text
              sx={{
                fontSize: '1.6rem',
                color: 'blue400',
                ':hover': {
                  color: 'white',
                },
              }}
            >
              {text}
            </Text>
          </ExternalLink>
        ) : (
          <Link key={text} to={to} aria-label={text}>
            <Text
              sx={{
                fontSize: '1.6rem',
                color: 'blue400',
                ':hover': {
                  color: 'white',
                },
              }}
            >
              {text}
            </Text>
          </Link>
        ),
      )}
    </>
  )
}

interface SocialMediaButtonProps {
  to: string
  icon: React.ElementType
}
function SocialMediaButton({ to, icon }: SocialMediaButtonProps): ReactElement {
  return (
    <ExternalLink
      aria-label={to}
      to={to}
      sx={{
        width: '3.2rem',
        height: '3.2rem',
        borderRadius: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
          '& svg path': {
            fill: 'white',
          },
        },
      }}
    >
      <Svg
        svg={icon}
        descriptiveText={`Follow us on ${
          to.includes('twitter') ? 'X/Twitter' : to.includes('reddit') ? 'Reddit' : 'Discord'
        }`}
        width="3.2rem"
        height="3.2rem"
      />
    </ExternalLink>
  )
}
